import {NotificationTemplate} from "@/notification-template/interfaces";
import Customer, {CustomerSummary} from "@/customer/interfaces/Customer";
import Consumer, {ConsumerLightWeight} from "@/core/interfaces/consumers/Consumer";
import {ProductSummary} from "@/product/interfaces/ProductSummary";

export interface NotificationSequence {
    id: number;
    name: string;
    description: string | null;
    messages: NotificationSequenceMessage[];
    sequenceables?: NotificationSequenceable[];
    is_enabled?: boolean;
}



export interface NotificationSequenceMessage {
    id: number;
    title: string;
    delay: number;
}

export interface NotificationSequenceDetails extends NotificationSequence {
    messages: NotificationSequenceMessageDetails[];
}

export interface NotificationSequenceMessageDetails extends NotificationSequenceMessage {
    template: NotificationTemplate;
}

export interface NotificationSequenceTargetedConsumer {
    active_instance: null | { id: number; ended_at: string | null; started_at: string; };
    consumer_key: string;
    sales: {
        name: string;
        code: string;
        id: number | null;
        date: string;
    }[];
    customer?: Customer;
    consumer?: Consumer;
}

export interface NotificationSequenceInstance {
    id: number;
    consumer_key: string;
    started_at: string;
    ended_at: string | null;
    customer?: Customer;
    consumer?: Consumer;
    trigger_purchases: NotificationSequenceTriggerPurchase[];
    message_instances: NotificationSequenceMessageInstance[];
}

export interface NotificationSequenceTriggerPurchase {
    id: number;
    product: ProductSummary;
    purchased_at: string;
}

export enum NotificationSequenceMessageInstanceStatus {
    NO_CUSTOMER = 'no_customer',
    NO_VALID_PHONE = 'no_valid_phone',
    NO_CREDIT = 'no_credit',
    PENDING = 'pending',
    SKIPPED = 'skipped',
    DISPATCHED = 'dispatched',
    FAILED = 'failed',
}

export interface NotificationSequenceMessageInstance {
    id: number;
    notification_sequence_message_id: number;
    sent_at?: string;
    status: NotificationSequenceMessageInstanceStatus;
    consumer: Omit<ConsumerLightWeight, 'nir'> | null;
    customer: CustomerSummary | null;
    sequence?: NotificationSequence;
    message?: NotificationSequenceMessage;
    created_at: string;
    updated_at: string;
}

/**
 * Upcoming sequence message.
 */
export interface UpcomingSequenceMessage extends NotificationSequenceMessage {
    send_at: string;
    sequence_instance_id: number;
    sequence: NotificationSequence;
    template: NotificationTemplate;
    consumer: Omit<ConsumerLightWeight, 'nir'> | null;
    customer: CustomerSummary | null;
}

/**
 * Weekly volume of upcoming sequence messages, to plot the scheduled sends timeline.
 */
export interface UpcomingSequenceMessageSummary {
    from: string;
    to: string;
    /** Number of messages, whatever their credit cost. */
    total: number;
    /** Credits the messages will consume: an SMS can cost several segments. */
    total_credits: number;
    weeks: UpcomingSequenceMessageWeek[];
}

export interface UpcomingSequenceMessageWeek {
    week_start: string;
    week_end: string;
    count: number;
    credits: number;
    sequences: {name: string; count: number}[];
}

export interface NotificationSequenceable {
    id: number;
    type: 'program' | 'survey';
    name?: string | null;
    logo?: string | null;
}
